// =============================================================================
// publication-client.ts — Client API ADMIN de la PUBLICATION du benchmark (S18 / stream C), DOUBLABLE.
// =============================================================================
// Câble la SPA admin contre les surfaces back livrées par stream C (PublicationController, AdminSessionGuard) :
//   POST /admin/campaigns/:id/analysis/validate   (Disponible → Validee)
//   GET  /admin/campaigns/:id/analysis/preview     (snapshot ANONYMISÉ : bands p25/p75/suppressed)
//   POST /admin/campaigns/:id/analysis/publish     body { confirm, confirmAgain } (409 si double-confirm manquant)
//
// Comme les autres clients UI (campaign-analysis, rank-admin…), expose une INTERFACE (PublicationClient)
// consommée par les hooks TanStack ; l'impl HTTP réelle (fetch) vient derrière la MÊME interface (DI seam,
// stub-first), substituée sans toucher l'UI.
//
// AUTORITÉ = API/worker : l'état de l'analyse (Disponible/Validee/Publiee), le calcul des bands (winsorization
// + percentiles + K-anonymity), l'irréversibilité de la publication sont CALCULÉS/PORTÉS en amont et LUS/RELAYÉS
// ici. L'UI ne porte AUCUNE règle métier : elle LIT et HABILLE. Le campaignId est TOUJOURS la ressource d'URL.
//
// DÉCISION GATE_HUMAN Cx-S18-5 (aperçu FIDÈLE) : le preview est le SNAPSHOT ANONYMISÉ — bands {profileCode,
// seniorityLevel, p25, p75, suppressed} + métadonnées de traçabilité. JAMAIS de siret / effectif /
// submissionCount / min / max. Aucun champ nominatif n'est déclaré dans ces DTO — le contrat front interdit
// structurellement d'afficher du nominatif (parité exacte de ce que `preview` renvoie).
//
// DTO = miroirs HTTP ASCII (PAS @jeko/db) : l'enum de séniorité est répliqué en union de littéraux (parité
// Prisma RateSeniorityLevel). Horodatages = ISO 8601 (string), jamais d'objet Date ORM.

import { HttpApiError } from '@/api/http-error';

/** Niveau de séniorité (parité Prisma RateSeniorityLevel, ASCII — accents = i18n front). */
export type RateSeniorityLevelDto = 'Debutant' | 'Maitrisant' | 'Expert' | 'Referent';

/**
 * État machine de l'analyse pertinent pour la publication (sous-ensemble de DBU §2.6 / ADR D10) :
 *   Disponible → Validee → Publiee. La VALIDATION (Disponible → Validee) est un prérequis de la
 *   PUBLICATION (Validee → Publiee, irréversible). Les états antérieurs (EnAttente/EnTraitement) sont
 *   représentés pour l'affichage défensif mais ne débloquent aucune action de publication.
 */
export type PublicationStateDto =
  | 'EnAttente'
  | 'EnTraitement'
  | 'Disponible'
  | 'Validee'
  | 'Publiee';

/**
 * État de publication du SNAPSHOT (distinct de l'état machine de l'analyse). Draft tant que non publié,
 * Published après la bascule IRRÉVERSIBLE. Correctif divergence C↔F (OPTION A) : ce champ ne gouverne PAS
 * l'activation des actions UI (c'est `analysisState` qui le fait) ; il est exposé pour transparence.
 */
export type SnapshotPublicationStateDto = 'Draft' | 'Published';

/**
 * UNE bande du snapshot ANONYMISÉ (aperçu FIDÈLE, décision Cx-S18-5). Miroir EXACT d'une `band` renvoyée par
 * `GET .../analysis/preview`. Les SEULS champs autorisés : couple (profileCode, seniorityLevel), bornes
 * AGRÉGÉES p25/p75, et le drapeau `suppressed` (gate K-anonymity : sous le seuil, p25/p75 = null + suppressed).
 *
 * INTERDIT (structurellement absent du type, jamais réintroduit côté front) : siret, effectif, submissionCount,
 * minEur, maxEur, raison sociale, TJM individuel — AUCUNE donnée nominative ni hors-agrégat.
 */
export interface PreviewBandDto {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /** P25 winsorized (entier EUR) ou `null` si la bande est supprimée (gate K-anon). */
  readonly p25: number | null;
  /** P75 winsorized (entier EUR) ou `null` si la bande est supprimée (gate K-anon). */
  readonly p75: number | null;
  /** true ⇒ bande sous le seuil K-anonymity : p25/p75 = null, bande masquée (Hors-volume). */
  readonly suppressed: boolean;
}

/**
 * Réponse de `GET .../analysis/preview` : le SNAPSHOT ANONYMISÉ fidèle (décision Cx-S18-5). Les métadonnées
 * de traçabilité (snapshotId / analysisId / catalogVersionId / basedOnCycle / computedAt / publicationState /
 * analysisState / kAnonymityThreshold) sont des identifiants/horodatages/états techniques — JAMAIS du
 * nominatif. Aucun siret, aucun effectif, aucun submissionCount, aucun min/max n'est déclaré.
 *
 * DEUX états DISTINCTS (correctif divergence C↔F, OPTION A) : `publicationState` = état du SNAPSHOT
 * (Draft/Published) ; `analysisState` = état machine de l'ANALYSE (Disponible→Validee→Publiee) qui GOUVERNE
 * l'activation de Valider/Publier. `analysisState` n'expose que le littéral d'état (anti-nominatif).
 *
 * V26-PUBLICATION-FINISH (DBU §2.6 l.1448) : `publishedAt`/`publishedBy` portent le badge post-publication
 * — date/heure de bascule + identité admin CONSERVÉES, re-lisibles après reload (l'aperçu est la requête que
 * l'écran refait à chaque chargement). Métadonnées de l'ACTE admin (traçabilité) ; `null` tant que non publiée.
 */
export interface PreviewSnapshotDto {
  readonly snapshotId: string;
  readonly campaignId: string;
  readonly analysisId: string;
  readonly catalogVersionId: string;
  /** Numéro du cycle sur lequel le snapshot est basé (C1=1, C2=2 — traçabilité). */
  readonly basedOnCycle: number;
  readonly computedAt: string;
  /** État de publication du SNAPSHOT (Draft/Published) — transparence, NE gouverne PAS l'UI. */
  readonly publicationState: SnapshotPublicationStateDto;
  /** État machine de l'ANALYSE (Disponible/Validee/Publiee) — gouverne Valider/Publier côté UI. */
  readonly analysisState: PublicationStateDto;
  /** Seuil K-anonymity appliqué (paramètre applicatif relayé pour transparence admin). */
  readonly kAnonymityThreshold: number;
  /** Bandes anonymisées (p25/p75/suppressed par couple) — l'aperçu FIDÈLE. */
  readonly bands: readonly PreviewBandDto[];
  /** Horodatage de bascule (ISO 8601). `null` tant que non publiée (badge post-publication, DBU §2.6 l.1448). */
  readonly publishedAt: string | null;
  /** Subject OIDC opaque de l'admin publicateur. `null` tant que non publiée (badge, DBU §2.6 l.1448). */
  readonly publishedBy: string | null;
}

/**
 * Résultat de la VALIDATION (Disponible → Validee). Miroir du POST .../analysis/validate. L'API DÉRIVE l'état
 * cible (Validee) ; le front relaie l'intention (campaignId d'URL) et reflète le nouvel état.
 */
export interface ValidateAnalysisResultDto {
  readonly campaignId: string;
  readonly publicationState: PublicationStateDto;
}

/**
 * Corps de la PUBLICATION IRRÉVERSIBLE (décision PP-08, double-confirm). Les DEUX drapeaux sont OBLIGATOIRES :
 * l'API répond 409 si l'un manque/est faux. Le front ne les pose à `true` qu'APRÈS la double confirmation
 * explicite de la modale friction max (case « Je comprends que c'est irréversible » + saisie « CONFIRMER »).
 */
export interface PublishInput {
  readonly confirm: boolean;
  readonly confirmAgain: boolean;
}

/**
 * Résultat de la PUBLICATION (Validee → Publiee, irréversible). Miroir du POST .../analysis/publish. `publishedAt`
 * = horodatage de bascule (ISO 8601). L'irréversibilité est l'autorité de l'API : aucun champ de « dé-publication ».
 */
export interface PublishResultDto {
  readonly campaignId: string;
  readonly publicationState: PublicationStateDto;
  readonly publishedAt: string;
}

/**
 * Corps de la DÉ-PUBLICATION (UX-03, DBU §2.6 l.1450). `motif` OBLIGATOIRE, non vide : il porte
 * l'intentionnalité du geste (fortement engageant + journalisé, DBU §2.6 l.1442). L'API répond 400
 * (`ANALYSIS_UNPUBLISH_MOTIF_REQUIRED`) si le motif est vide ; le front désactive déjà le bouton dans ce cas.
 */
export interface UnpublishInput {
  readonly motif: string;
}

/**
 * Résultat de la DÉ-PUBLICATION (Published → Draft, DBU §2.6 l.1450 : re-bascule `publication_state='Draft'`
 * en cas d'anomalie découverte post-publication). Miroir du POST .../analysis/unpublish. La visibilité Pulse
 * est RETIRÉE ; `unpublishedAt`/`unpublishedBy` tracent l'ACTE admin (journalisation). L'autorité de la bascule
 * (transition, journalisation, RLS) reste l'API/worker : le front relaie l'intention (campaignId d'URL + motif).
 */
export interface UnpublishResultDto {
  readonly campaignId: string;
  readonly analysisId: string;
  readonly snapshotId: string;
  /** État du snapshot après dé-publication : toujours `Draft` (retrait de la visibilité Pulse). */
  readonly publicationState: SnapshotPublicationStateDto;
  /** Horodatage de la dé-publication (ISO 8601) — traçabilité de l'acte admin. */
  readonly unpublishedAt: string;
  /** Subject OIDC opaque de l'admin dé-publicateur — server-set (session), jamais fabriqué côté front. */
  readonly unpublishedBy: string;
}

/**
 * Erreur applicative renvoyée par l'API admin publication (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle
 * ne réinterprète JAMAIS la règle métier (autorité = API/worker). Le 409 (double-confirm manquant, déjà
 * publié, mauvais état, conflit de dé-publication) remonte avec le message serveur tel quel.
 *
 * `code` : code d'erreur applicatif STABLE (ex. `ANALYSIS_UNPUBLISH_MOTIF_REQUIRED`, `ANALYSIS_NOT_PUBLISHED`,
 * `ANALYSIS_UNPUBLISH_CONFLICT`) quand l'API en émet un. Il permet à l'UI de composer un message ACTIONNABLE
 * pour l'utilisateur (sans réinterpréter la règle : le code est un identifiant de cas, pas une décision métier).
 */
export class PublicationApiError extends HttpApiError {
  public readonly code: string | undefined;
  public constructor(message: string, status?: number, code?: string) {
    super(message, status, 'PublicationApiError');
    this.code = code;
  }
}

/**
 * Contrat du client publication ADMIN — miroir des surfaces back stream C (AdminSessionGuard). Aucune règle
 * métier ici : transitions d'état, anonymisation des bands, irréversibilité, exigence du double-confirm sont
 * l'autorité de l'API/worker. L'UI relaie l'intention (campaignId d'URL + drapeaux de confirmation).
 */
export interface PublicationClient {
  /** GET .../analysis/preview — SNAPSHOT ANONYMISÉ (bands p25/p75/suppressed + métadonnées). */
  getPreview(campaignId: string): Promise<PreviewSnapshotDto>;
  /** POST .../analysis/validate — Disponible → Validee (prérequis de la publication). */
  validateAnalysis(campaignId: string): Promise<ValidateAnalysisResultDto>;
  /**
   * POST .../analysis/publish — Validee → Publiee, IRRÉVERSIBLE. `input` porte le DOUBLE confirm
   * ({confirm, confirmAgain}) ; l'API répond 409 si l'un manque (PublicationApiError, message serveur).
   */
  publish(campaignId: string, input: PublishInput): Promise<PublishResultDto>;
  /**
   * POST .../analysis/unpublish — DÉ-PUBLICATION (Published → Draft, DBU §2.6 l.1450). `input.motif` est
   * OBLIGATOIRE (400 `ANALYSIS_UNPUBLISH_MOTIF_REQUIRED` si vide). 409 (`ANALYSIS_NOT_PUBLISHED` /
   * `ANALYSIS_UNPUBLISH_CONFLICT`) et 404 (aucune analyse) remontent en PublicationApiError (code + message).
   */
  unpublish(
    campaignId: string,
    input: UnpublishInput,
  ): Promise<UnpublishResultDto>;
}
